import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Link,
} from '@mui/material';
import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trackButtonClick } from '../utils/analytics';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            trackButtonClick('Back to Home', 'Privacy Policy');
            navigate('/');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <Home fontSize="small" />
          Back to Home
        </Link>
      </Box>

      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Introduction
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Bufferio App ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our clipboard sharing service, including our web application (bufferio.app) and Chrome extension.
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            By using Bufferio App, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Information We Collect
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Authentication Information
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            When you sign in using Google Sign-In, we collect your Google account email address and basic profile information (name and profile picture). This information is used solely for user identification and account management.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Device Information
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We collect device identifiers, device names, and device types (Web, Chrome Extension, Android, iOS) to manage your registered devices and enable content sharing between them.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Message Content
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            When you send content through Bufferio App, we temporarily store the content (text, images, files) you explicitly choose to share. This content is used solely for delivering it to your selected recipient devices or for generating 6-digit codes for sharing with non-registered devices.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Usage Data
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We may collect information about how you interact with our service, such as when you send or receive messages, to improve our service and troubleshoot issues.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            How We Use Your Information
          </Typography>
          <Typography variant="body1" color="text.secondary" component="div">
            <ul style={{ paddingLeft: '20px', margin: '16px 0' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>To provide our service:</strong> We use your information to enable cross-device content sharing, manage your devices, and deliver messages.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>To authenticate users:</strong> We use Google authentication to securely identify users and manage their accounts.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>To improve our service:</strong> We analyze usage patterns to enhance functionality and fix bugs.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>To communicate with you:</strong> We may use your email address to send important service updates or respond to support requests.
              </li>
            </ul>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Data Storage and Security
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Local Storage:</strong> Authentication tokens, device IDs, and user preferences are stored locally on your device using encrypted storage (Chrome's secure storage for extensions, localStorage for web app). This data never leaves your device except for API authentication purposes.
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Server Storage:</strong> Message content is stored on our secure servers to enable synchronization between your devices. All data transmitted to our servers is encrypted using HTTPS/TLS protocols.
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Data Retention:</strong> Messages are stored until you delete them or until your account is deleted. You can delete individual messages or clear all messages at any time through the application interface.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We do not sell, trade, or rent your personal information to third parties. We only share your information in the following circumstances:
          </Typography>
          <Typography variant="body1" color="text.secondary" component="div">
            <ul style={{ paddingLeft: '20px', margin: '16px 0' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>With Google:</strong> We use Google Sign-In for authentication. Google's use of your information is governed by their Privacy Policy.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Service Providers:</strong> We may share data with third-party service providers who assist us in operating our service (e.g., cloud hosting providers), subject to strict confidentiality agreements.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and the safety of our users.
              </li>
            </ul>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Permissions and Data Access
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Our Chrome extension requests the following permissions:
          </Typography>
          <Typography variant="body1" color="text.secondary" component="div">
            <ul style={{ paddingLeft: '20px', margin: '16px 0' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>identity:</strong> Required for Google Sign-In authentication. We only request your email and profile information.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>storage:</strong> Used to store authentication tokens and device information locally on your device.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>contextMenus:</strong> Enables right-click menu options to send text and images from web pages.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>notifications:</strong> Allows desktop notifications when you receive new messages.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>activeTab:</strong> Required to access the current tab when using context menu to send selected content.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>downloads:</strong> Enables downloading files you receive through the extension.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>clipboardWrite:</strong> Allows copying received content to your clipboard.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>host_permissions:</strong> Required to communicate with our API server (api.bufferio.app) and Google authentication services.
              </li>
            </ul>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            What We Don't Do
          </Typography>
          <Typography variant="body1" color="text.secondary" component="div">
            <ul style={{ paddingLeft: '20px', margin: '16px 0' }}>
              <li style={{ marginBottom: '8px' }}>We do not track your browsing history</li>
              <li style={{ marginBottom: '8px' }}>We do not collect personal information beyond what's needed for authentication and service delivery</li>
              <li style={{ marginBottom: '8px' }}>We do not share your data with advertisers</li>
              <li style={{ marginBottom: '8px' }}>We do not access websites you visit except when you explicitly use the context menu feature</li>
              <li style={{ marginBottom: '8px' }}>We do not sell or monetize your personal data</li>
            </ul>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Your Rights and Choices
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You have the following rights regarding your personal information:
          </Typography>
          <Typography variant="body1" color="text.secondary" component="div">
            <ul style={{ paddingLeft: '20px', margin: '16px 0' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Access:</strong> You can view your account information and messages through the application interface.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Deletion:</strong> You can delete individual messages, clear all messages, or delete your account and all associated data at any time.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Device Management:</strong> You can view, rename, or delete registered devices at any time.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Account Deletion:</strong> You can request account deletion by contacting us at support@bufferio.app.
              </li>
            </ul>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Children's Privacy
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at support@bufferio.app.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Contact Us
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Email:</strong>{' '}
            <Link href="mailto:support@bufferio.app" color="primary">
              support@bufferio.app
            </Link>
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Website:</strong>{' '}
            <Link href="https://bufferio.app" color="primary" target="_blank" rel="noopener noreferrer">
              https://bufferio.app
            </Link>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              trackButtonClick('Back to Home', 'Privacy Policy');
              navigate('/');
            }}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            ← Back to Home
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;

